import type { PrivateFile } from './document';

// needed for zod
export const MEDICAL_VISIT_OPINION_VALUES = [
  'APTE',
  'APTE_AVEC_RESERVES',
  'INAPTE',
] as const;

export enum MedicalVisitOpinionEnum {
  APTE = 'APTE',
  APTE_AVEC_RESERVES = 'APTE_AVEC_RESERVES',
  INAPTE = 'INAPTE',
}

export type MedicalVisitOpinion = (typeof MEDICAL_VISIT_OPINION_VALUES)[number];

export const MEDICAL_VISIT_STATUS_VALUES = [
  'VALID',
  'EXPIRE_SOON',
  'EXPIRED',
] as const;

export enum MedicalVisitStatusEnum {
  VALID = 'VALID',
  EXPIRE_SOON = 'EXPIRE_SOON',
  EXPIRED = 'EXPIRED',
}

export type MedicalVisitStatus = (typeof MEDICAL_VISIT_STATUS_VALUES)[number];

export type MedicalVisitType = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  code: string;
  label: string;
  periodicityMonths: number | null;
  // Display-only "1st visit due within X months of joining" (0 = before
  // taking up the post) — no anchor date exists to compute a real due date.
  // Only affects compliance severity, see UserComplianceService.
  initialDeadlineMonths: number | null;
  // Pre-checked when creating a new employee (still editable there) — a UX
  // default only, not an org-wide "mandatory" flag. Several types can be
  // default at once (e.g. two visits required by the same regulation).
  isDefault: boolean;
  organizationId: number;
  // Whether at least one MedicalVisit has been recorded against this type —
  // present only on the list (findAllForOrg), not on create/update/findOne
  // responses. Blocks deletion; unlike UserMedicalVisitType assignments,
  // which are freely removed on delete.
  hasRecordedVisits?: boolean;
};

// A type only carries regulatory weight for the employees it's explicitly
// attached to — there is no org-wide "mandatory" flag, see
// UserComplianceService.
export type UserMedicalVisitType = {
  id: number;
  createdAt: Date;
  userId: number;
  typeId: number;
  organizationId: number;
};

export type MedicalVisitUser = {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
};

export type MedicalVisit = {
  id: number;
  createdAt: Date;
  updatedAt: Date;
  date: Date;
  nextDueDate: Date | null;
  opinion: MedicalVisitOpinion;
  restriction: string | null;
  status: MedicalVisitStatus;
  // relations
  organizationId: number;
  typeId: number;
  type: MedicalVisitType;
  userId: number;
  user: MedicalVisitUser;
  createdById: number;
  files: PrivateFile[];
};
